import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { SalesPortalService } from 'src/app/services/sales-portal.service';

type TableType = 'turnover' | 'workshop-acquisition';
export type TimeMode = 'BY_MONTH' | 'BY_QUARTER' | 'BY_YEAR' | '';
export type ClassificationMethod = 'PRODUCT_GROUP' | 'CBF' | 'PACKAGE';
export type SplittingMethod = 'manualSplitting' | 'automaticallySplitting';

@Component({
  selector: 'product-group-table',
  templateUrl: './product-group-table.component.html',
  styleUrls: ['./product-group-table.component.less'],
})
export class ProductGroupTableComponent implements OnInit, OnChanges {
  @Input() tableType: TableType = 'turnover';
  @Input() year: string = '';
  @Input() timeMode: TimeMode = 'BY_QUARTER';
  @Input() productGroup: string = '';
  @Input() cbf: string = '';
  @Input() cbfDesc: string = '';
  @Input() targetAmount: number = 0;
  @Input() classificationMethod: ClassificationMethod = 'PRODUCT_GROUP';
  @Input() splittingMethod: SplittingMethod = 'manualSplitting';
  @Input() tableData: any = [];
  @Input() disableEdit: boolean = false;
  @Input() detailInfo: any = {};

  constructor(public salesPortalService: SalesPortalService) {}

  ngOnChanges(sc: SimpleChanges) {
    if ('splittingMethod' in sc) {
      const splittingMethod = sc['splittingMethod'].currentValue;
      this.onSplittingMethodChange(splittingMethod);
    }
  }

  get canEdit() {
    return (
      this.splittingMethod !== 'automaticallySplitting' && !this.disableEdit
    );
  }

  get remainingUndivided() {
    const rowTotalTargetAmount = this.tableData.reduce(
      (
        acc: any,
        current: {
          targetAmount: any;
        }
      ) => acc + current.targetAmount,
      0
    );

    return Number((this.targetAmount - rowTotalTargetAmount)?.toFixed(2));
  }

  get columns() {
    if (this.timeMode === 'BY_QUARTER') {
      return this.columnsQuarter;
    } else if (this.timeMode === 'BY_MONTH') {
      return this.columnsMonth;
    } else if (this.timeMode === 'BY_YEAR') {
      return [{ name: this.year }];
    } else {
      return this.columnsQuarter;
    }
  }

  columnsQuarter = [
    { name: 'Q1' },
    { name: 'Q2' },
    { name: 'Q3' },
    { name: 'Q4' },
  ];

  columnsMonth = [
    { name: 'Jan' },
    { name: 'Feb' },
    { name: 'Mar' },
    { name: 'Apr' },
    { name: 'May' },
    { name: 'Jun' },
    { name: 'Jul' },
    { name: 'Aug' },
    { name: 'Sep' },
    { name: 'Oct' },
    { name: 'Nov' },
    { name: 'Dec' },
  ];

  ngOnInit() {}

  private onSplittingMethodChange(newValue: SplittingMethod) {
    if (newValue === 'automaticallySplitting') {
      if (this.timeMode === 'BY_QUARTER') {
        this.handleAutomaticallyDataByQuarter();
      } else if (this.timeMode === 'BY_MONTH') {
        this.handleAutomaticallyDataByMonth();
      } else if (this.timeMode === 'BY_YEAR') {
        this.handleAutomaticallyDataByYear();
      }
    }
  }

  handleGetTarget() {
    let target;
    if (this.tableType === 'turnover') {
      target =
        this.detailInfo.classificationMethod === 'PACKAGE'
          ? this.detailInfo.targetProcurement
          : this.targetAmount;
    } else {
      target = this.targetAmount;
    }
    return target;
  }

  // Automatic allocation logic in workshop
  workshopSplitIntoParts(number: number, parts: number) {
    let result = new Array(parts).fill(Math.floor(number / parts));
    let remainder = number % parts;

    for (let i = 0; i < remainder; i++) {
      result[i]++;
    }

    return result;
  }

  handleAutomaticallyDataByQuarter() {
    const target = this.handleGetTarget();
    if (this.tableType === 'turnover') {
      const dividedData = Number((target / 4)?.toFixed(2));
      const remainData = Number((target - dividedData * 3)?.toFixed(2));
      this.tableData = this.tableData.map(
        (item: { targetAmount: number }, index: number) => {
          if (index < 3) {
            item.targetAmount = dividedData;
          } else {
            item.targetAmount = remainData;
          }
          return item;
        }
      );
    } else {
      const list = this.workshopSplitIntoParts(target, 4);
      this.tableData = this.tableData.map(
        (item: { targetAmount: number }, index: number) => {
          item.targetAmount = list[index];
          return item;
        }
      );
    }
  }

  handleAutomaticallyDataByMonth() {
    const target = this.handleGetTarget();
    if (this.tableType === 'turnover') {
      const dividedData = Number((target / 12)?.toFixed(2));
      const remainData = Number((target - dividedData * 11)?.toFixed(2));
      this.tableData = this.tableData.map(
        (item: { targetAmount: number }, index: number) => {
          if (index < 11) {
            item.targetAmount = dividedData;
          } else {
            item.targetAmount = remainData;
          }
          return item;
        }
      );
    } else {
      const list = this.workshopSplitIntoParts(target, 12);
      this.tableData = this.tableData.map(
        (item: { targetAmount: number }, index: number) => {
          item.targetAmount = list[index];
          return item;
        }
      );
    }
  }

  handleAutomaticallyDataByYear() {
    this.tableData = this.tableData.map((item: { targetAmount: number }) => {
      item.targetAmount = this.handleGetTarget();
      return item;
    });
  }
}
